import React, {useEffect, useState} from "react";
import {getShippingOptions, useInfiniteShippingCostPresets} from "../../Api/ShipppingCalculatorEndpoints";
import {Controller, useForm} from "react-hook-form";
import {FormWrapperLabel} from "../../Framework/Components/Forms/FormEntryWrapper/FormEntryWrapper";
import {CountryCodes} from "../../Framework/Data/CountryCodes";
import Button, {ButtonSubmit} from "../../Framework/Components/Buttons/Button";
import royalMailLogo from '../../Images/royal-mail-logo.svg';
import dhlLogo from '../../Images/dhl-logo.svg';
import dpdLogo from '../../Images/dpd-logo.svg';
import {ColourMod} from "../../Framework/Data/Enums/LayoutEnums";
import {FormatCurrency} from "../../Framework/Constants/Helpers";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowsLeftRight, faArrowsUpDown, faArrowUpRightAndArrowDownLeftFromCenter,
 faTruckFast,
    faWeightScale
} from "@fortawesome/pro-solid-svg-icons";
import ComboBox from "../../Framework/Components/Forms/ComboBox/ComboBox";
import {SelectItemType} from "../../Framework/Types/FormTypes";
import GenericLoader from "../../Framework/Components/Loaders/GenericLoader";

const defaultShipping = {
    weight: 0.0,
    length: 0.0,
    depth: 0.0,
    width: 0.0,
    countryCode: 'GB',
};

type ShippingPresetItemType = {
    depth: number;
    height: number;
    mailItemSizeDescription: string;
    mailItemSizeId: number;
    mailTypeId:number;
    weight: number;
    width: number;
}

type ShippingOptionType = {
    description: string;
    durationDescription: string;
    minDaysToDeliver: number;
    maxDaysToDeliver: number;
    cost: number;
    costWithTax: number;
}

const ShippingCalculator = () => {
    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        register,
        formState: { errors, isValid },
    } = useForm({
        defaultValues: defaultShipping,
        mode: "onChange"
    });

    const [ shippingValues, setShippingValues ] = useState<any>(null);
    const [ updateShipping, setUpdateShipping ] = useState<boolean>(false);

    const { data: presetsData, status: presetsStatus, error: presetsError } : any = useInfiniteShippingCostPresets();

    const [ shapedData, setShapedData] = useState<any>(null);
    const [ countryCodeData, setCountryCodeData] = useState<any>(null);

    const [ weightUnit, setWeightUnit] = useState<string>('G');
    const [ measurementUnit, setMeasurementUnit] = useState<string>('CM');

    const CalculateMeasurements = (weight: number, width: number, length: number, depth: number, ignoreConversion?: boolean) => {
        return {
            weight: ignoreConversion ? weight : weightUnit === 'KG' ? weight * 1000 : weight,
            width: ignoreConversion ? width : measurementUnit === 'CM' ? getValues().width : getValues().width * 2.54,
            length: ignoreConversion ? length : measurementUnit === 'CM' ? getValues().length : getValues().length * 2.54,
            depth: ignoreConversion ? depth : measurementUnit === 'CM' ? getValues().depth : getValues().depth * 2.54,
            countryCode: getValues().countryCode,
        }
    }

    const HandleSend = (data: any) => {
        setShippingValues(CalculateMeasurements(parseInt(data.weight), parseInt(data.width), parseInt(data.length), parseInt(data.depth)));
        setUpdateShipping(!updateShipping);
    }

    const SetPresetData = (option: SelectItemType) => {
        const item : ShippingPresetItemType = presetsData.pages.map((page: any) =>
            page.data.filter((item: ShippingPresetItemType) => item.mailItemSizeDescription === option.label)[0]
        )[0];

        if(item) {
            setValue('weight', item.weight);
            setValue('width', item.width);
            setValue('length', item.height);
            setValue('depth', item.depth);

            setShippingValues(CalculateMeasurements(item.weight, item.width, item.height, item.depth, true));
            setUpdateShipping(!updateShipping);
        }
    }

    const ShapePresetsData = () => {
        let selectOptions: Array<SelectItemType> = [];

        if(!presetsData) return selectOptions;

        presetsData.pages.map((page: any) =>
            page.data.map((item: ShippingPresetItemType, index: number) => selectOptions.push({label: item.mailItemSizeDescription, value: index}))
        )

        return selectOptions;
    }

    const ShapeCountryCodeData = () => {
        let selectOptions: Array<SelectItemType> = [];

        CountryCodes.map((item: any) => selectOptions.push({label: item.label, value: item.value}))

        return selectOptions;
    }

    useEffect(() => {
        document.title = 'Shipping Calculator';
        setCountryCodeData(ShapeCountryCodeData());
    }, []);

    useEffect(() => {
        if(presetsData) {
            setShapedData(ShapePresetsData());
        }
    }, [presetsData]);


    return (
        <div className={'inner-content'}>

            <div className='content-main'>
                <div className={'container-narrow container-center mt-lg '}>

                    <h1>Shipping Calculator</h1>

                    <div className={'row mb-lg'}>
                        <div className={'col-xs-12 col-md-5'}>

                            <form onSubmit={handleSubmit((data, e) => HandleSend(data))} className={'sticky'}>
                                <fieldset>
                                    <h5 className={'mb-0'}>Destination</h5>
                                    <p>Where would you like to post your item?</p>

                                    <div className={'mb-xl'}>
                                        <Controller
                                            control={control}
                                            name="countryCode"
                                            rules={{ required: true }}
                                            render={({field, fieldState}) =>
                                                <ComboBox
                                                    name={'countryDestination'}
                                                    label={'Country'}
                                                    onChange={(val: SelectItemType) => field.onChange(val.value)}
                                                    data={countryCodeData}
                                                    placeholder={'Select Country...'}
                                                    isLoading={!countryCodeData}
                                                    valueType={'string'}
                                                    defaultValues={['GB']}
                                                />
                                            } />
                                    </div>

                                    <h5 className={'mb-0'}>Item Weight & Size</h5>
                                    <p>Enter the weight and dimensions of your items</p>

                                    <div className={'flex-column flex-gap-md mb-md'}>
                                        {presetsStatus === "error" ? (
                                            <span>Error: {presetsError.message}</span>
                                        ) : (
                                            <ComboBox
                                                name={'shippingPresets'}
                                                label={'Item Presets'}
                                                onChange={(e: SelectItemType) => SetPresetData(e)}
                                                data={shapedData}
                                                placeholder={'Select Preset...'}
                                                isLoading={!shapedData || presetsStatus === "loading" || presetsError}
                                                valueType={'number'}
                                            />
                                        )}

                                        <div className={'flex-row flex-gap-md'}>
                                            <FormWrapperLabel
                                                label={'Weight'}
                                                value={getValues().weight}
                                                invalid={errors.weight && errors.weight?.message !== ''}
                                                validationMessage={errors.weight?.message as string}
                                            >
                                                <div className={'input-addon addon-first'}>
                                                    <div title={weightUnit} className={'input-addon-icon'}>
                                                        <FontAwesomeIcon icon={faWeightScale} fixedWidth={true} />
                                                    </div>
                                                    <input type='number' step='0.01' aria-invalid={errors.weight ? 'true' : 'false'} {...register('weight', { required: "This is a required field", min: 0.01 }) } />
                                                </div>
                                            </FormWrapperLabel>

                                            <div className={'form-group'}>
                                                <label>{weightUnit === 'G' ? 'Grams' : 'Kilograms'}</label>
                                                <div className={'btn-group mb-0'}>
                                                    <Button title={'G'} onClick={() => setWeightUnit('G')} colour={weightUnit === 'G' ? ColourMod.Selected : ColourMod.Secondary} />
                                                    <Button title={'KG'} onClick={() => setWeightUnit('KG')} colour={weightUnit === 'KG' ? ColourMod.Selected : ColourMod.Secondary} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className={'flex-row flex-gap-md'}>
                                            <FormWrapperLabel
                                                label={'Width'}
                                                value={getValues().width}
                                                invalid={errors.width && errors.width?.message !== ''}
                                                validationMessage={errors.width?.message as string}
                                            >
                                                <div className={'input-addon addon-first'}>
                                                    <div title={measurementUnit} className={'input-addon-icon'}>
                                                        <FontAwesomeIcon icon={faArrowsLeftRight} fixedWidth={true} />
                                                    </div>
                                                    <input type='number' step='0.01' aria-invalid={errors.width ? 'true' : 'false'} {...register('width', { required: "This is a required field", min: 0.5 })} />
                                                </div>
                                            </FormWrapperLabel>

                                            <FormWrapperLabel
                                                label={'Height'}
                                                value={getValues().depth}
                                                invalid={errors.depth && errors.depth?.message !== ''}
                                                validationMessage={errors.depth?.message as string}
                                            >
                                                <div className={'input-addon addon-first'}>
                                                    <div title={measurementUnit} className={'input-addon-icon'}>
                                                        <FontAwesomeIcon icon={faArrowsUpDown} fixedWidth={true} />
                                                    </div>
                                                    <input type='number' step='0.01' aria-invalid={errors.depth ? 'true' : 'false'} {...register('depth', { required: "This is a required field", min: 0.5  })} />
                                                </div>
                                            </FormWrapperLabel>

                                            <FormWrapperLabel
                                                label={'Depth'}
                                                value={getValues().length}
                                                invalid={errors.length && errors.length?.message !== ''}
                                                validationMessage={errors.length?.message as string}
                                            >
                                                <div className={'input-addon addon-first'}>
                                                    <div title={measurementUnit} className={'input-addon-icon'}>
                                                        <FontAwesomeIcon icon={faArrowUpRightAndArrowDownLeftFromCenter} fixedWidth={true} />
                                                    </div>
                                                    <input type='number' step='0.01' aria-invalid={errors.length ? 'true' : 'false'} {...register('length', { required: "This is a required field", min: 0.5  })} />
                                                </div>
                                            </FormWrapperLabel>

                                            <div className={'form-group'}>
                                                <label>{measurementUnit === 'CM' ? 'Centimetre' : 'Inches'}</label>
                                                <div className={'btn-group mb-0'}>
                                                    <Button title={'CM'} onClick={() => setMeasurementUnit('CM')} colour={measurementUnit === 'CM' ? ColourMod.Selected : ColourMod.Secondary} />
                                                    <Button title={'IN'} onClick={() => setMeasurementUnit('IN')} colour={measurementUnit === 'IN' ? ColourMod.Selected : ColourMod.Secondary} />
                                                </div>
                                            </div>

                                        </div>


                                    </div>

                                    <ButtonSubmit title={'Get Rate'} disabled={!isValid} fullWidth={true} colour={ColourMod.Primary} />

                                </fieldset>
                            </form>
                        </div>

                        <div className={'col-xs-12 col-md-7'}>
                            {shippingValues ? (
                                <ShippingOptions
                                    updateValues={updateShipping}
                                    width={shippingValues.width}
                                    depth={shippingValues.depth}
                                    length={shippingValues.length}
                                    weight={shippingValues.weight}
                                    countryCode={shippingValues.countryCode}
                                />
                            ) : (
                                <div className={'empty-state-container text-left'}>
                                    <FontAwesomeIcon icon={faTruckFast} fixedWidth={true} size={"3x"} />
                                    <h6 className={'sm-sub-heading mt-sm mb-0'}>Shipping Options</h6>
                                    <ol>
                                        <li>Select your destination</li>
                                        <li>Fill in your item details</li>
                                    </ol>
                                </div>
                            )}

                        </div>
                    </div>

                </div>
            </div>

        </div>
    )
}

export default ShippingCalculator

type ShippingOptionsType = {
    updateValues: boolean;
    countryCode: string;
    width: number;
    length: number;
    depth: number;
    weight: number;
}

const ShippingOptions = (props: ShippingOptionsType) => {
    const [shippingOptions, setShippingOptions] = useState<any>(null);
    const [groupData, setGroupData] = useState<any>(null);

    const FetchOptions = async () => {
        const options = await getShippingOptions(props.countryCode, props.width, props.length, props.depth, props.weight);
        setShippingOptions(options);
    }

    const MapCourierLogo = (props: any) => {
        if(props.value.indexOf('DPD') > -1) {
            return <img src={dpdLogo} alt={'DPD Logo'} style={{ maxWidth : 'calc(var(--xl-unit) * 2)'}} />
        } else if(props.value.indexOf('DHL') > -1) {
            return <img src={dhlLogo} alt={'DHL Logo'} style={{ maxWidth : 'calc(var(--xl-unit) * 2)'}} />
        }

        return <img src={royalMailLogo} alt={'Royal Mail Logo'} style={{ maxWidth : 'calc(var(--xl-unit) * 2)' }} />
    }

    const GroupData = (data: any) => {
        const groups = data.reduce((groups: any, item: any) => {
            const title = item.description.split(' ')[0];

            if (!groups[title]) {
                groups[title] = [];
            }

            groups[title].push(item);

            return groups;
        }, {});

        setGroupData(groups);
    }

    const RenderGroup = (data: any) => {
        let groups = [];

        for (const [key, value] of Object.entries(data) as any) {
            groups.push(
                <div className={'card mb-xl pb-xs'} key={`shippingGroup${Math.floor(Math.random() * (20000 - 19000 + 1) + 19000)}`}>
                    <div className={'flex-column sticky'}>
                        <div className={'flex-row p-md glass'}>
                            <MapCourierLogo value={key} />
                            <h5 className={'mb-0 ml-md'}>{key === 'Royal' ? 'Royal Mail' : key}</h5>
                        </div>
                        <div className={'item-row item-row-header slim'} style={{ borderBlockWidth: 1, borderBlockStartStyle: 'solid', gridTemplateColumns: '1fr 12em 6em 6em' }}>
                            <div className={'pl-md'}>Description</div>
                            <div>Est. Delivery Time</div>
                            <div>Cost</div>
                            <div>Inc. VAT</div>
                        </div>
                    </div>

                    {value.map((item: ShippingOptionType, index: number) =>
                        <div className={'item-row item-row-highlighted slim'} key={`shippingOption${index}`} style={{ gridTemplateColumns: '1fr 12em 6em 6em' }}>
                            <div className={'pl-md'}>{item.description}</div>
                            <div>{item.durationDescription}</div>
                            <div>{FormatCurrency(item.cost)}</div>
                            <div>{FormatCurrency(item.costWithTax)}</div>
                        </div>
                    )}
                </div>
            )
        }

        return (
            <>
                {groups}
            </>
        )
    }

    useEffect(() => {
        if(shippingOptions?.data) {
            GroupData(shippingOptions.data);
        }
    }, [shippingOptions]);

    useEffect(() => {
        FetchOptions().then(() => null);
    }, [props.updateValues]);

    if(!groupData) return null;

    return (
        <>
            {!groupData ? (
                <GenericLoader label={'Shipping Options Loading'} />
            ) : (
                <>

                    <RenderGroup {...groupData} />
                    <ol>
                        <li>All shipping rates are shown in Great Britain Pounds (GBP).</li>
                        <li>These prices include UK VAT @ 20%, but if you’re outside of the UK and operate as an Incorporated Business or Registered Charity, you won’t be charged VAT.</li>
                        <li>Our Shipping Calculator provides an estimate of the shipping total cost.</li>
                        <li>All shipping cost quoted are DDU (Delivery Duty Unpaid), unless explicitly stated. You may be liable for VAT/Duty on delivery of your items/goods in the recipients destination country.</li>
                        <li>We will calculate the final shipping total cost when forwarding your items based on the actual weight and dimensions of your package.</li>
                        <li>All services may be subject to the following additional charges depending on your item specifics and destination, including but not limited to:
                            <ol className={'roman-numeral-list'}>
                                <li>Elevated Risk & Restricted Destination Surcharge</li>
                                <li>Emergency Situation Surcharge</li>
                                <li>Export Validation</li>
                                <li>Parcel Handling / Oversized Piece</li>
                                <li>Air Carrier Supplement Charges</li>
                            </ol>
                        </li>
                    </ol>
                </>
            )}
        </>
    )

}